import * as Sentry from '@sentry/react';
import { nvStorage } from '@simwood/nv-react-components';
import { isElectron } from '../../shared/helpers/platform';

const audioPath =
  process.env.NODE_ENV === 'production' && isElectron()
    ? 'static://assets/audio'
    : './assets/audio';

export const inboundRingtones = [
  { name: 'Default', fileName: 'inbound-ringing.mp3' },
  { name: "Classic 90's", fileName: 'classic-90s-ring.mp3' },
  { name: 'Digital Phone', fileName: 'digital-phone-ring.mp3' },
  { name: 'Marimba 1', fileName: 'marimba-ringtone-6.mp3' },
  { name: 'Marimba 2', fileName: 'ringing.mp3' },
  { name: 'Marimba 3', fileName: 'ringtone.mp3' },
  { name: 'Nortel', fileName: 'nortel-phone-ring.mp3' },
  { name: 'Bubbly Bubbles', fileName: 'ringtone-bubbly-bubbles.mp3' },
];

let currentInboundRingtone =
  nvStorage.get('selectedInboundRingtone') || inboundRingtones[0].fileName;

let currentOutputVolume: number = Number(nvStorage.get('volume') ?? 1);
let currentInboundRingingVolume: number = Number(
  nvStorage.get('inboundRingingVolume') ?? 1,
);

let audioDiagnosticsEnabled = false;

export const setAudioDiagnosticsEnabled = (enabled: boolean) => {
  audioDiagnosticsEnabled = enabled;
};

class AudioContextRinger {
  private ctx: AudioContext;
  private gainNode: GainNode;
  private buffer: AudioBuffer | null = null;
  private source: AudioBufferSourceNode | null = null;
  // pause() must cancel a play() still awaiting resume/load
  private wantPlaying = false;
  private bufferLoading: Promise<AudioBuffer> | null = null;
  private currentUrl: string;
  private label: string;

  constructor(url: string, label: string) {
    this.label = label;
    this.ctx = new AudioContext();
    this.gainNode = this.ctx.createGain();
    this.gainNode.connect(this.ctx.destination);
    this.currentUrl = url;

    this.ctx.addEventListener('statechange', () => {
      console.debug('[AudioContextRinger] statechange:', this.ctx.state, label);
      if (audioDiagnosticsEnabled) {
        Sentry.addBreadcrumb({
          category: 'audio',
          message: `AudioContext statechange: ${this.ctx.state}`,
          level: this.ctx.state === 'suspended' ? 'warning' : 'info',
          data: { label, url, state: this.ctx.state },
        });
      }
    });

    const loading = this._fetchBuffer(url);
    this.bufferLoading = loading;
    loading
      .then((buf) => {
        if (this.bufferLoading !== loading) return;
        this.buffer = buf;
        this.bufferLoading = null;
      })
      .catch((err) => {
        console.error('[AudioContextRinger] load error', err);
        if (this.bufferLoading === loading) this.bufferLoading = null;
        Sentry.captureException(
          err instanceof Error ? err : new Error(String(err)),
          { data: { label, url } },
        );
      });
  }

  private _fetchBuffer(url: string): Promise<AudioBuffer> {
    return fetch(url)
      .then(async (r) => {
        if (!r.ok) throw new Error(`${r.status} ${await r.text()} (${url})`);
        return r.arrayBuffer();
      })
      .then((ab) => this.ctx.decodeAudioData(ab));
  }

  setGain(gain: number): void {
    this.gainNode.gain.value = gain;
  }

  async setSinkId(sinkId: string): Promise<void> {
    if (!('setSinkId' in this.ctx)) return;
    // AudioContext.setSinkId() requires "" for the system default; "default" is
    // only a valid sentinel for HTMLMediaElement.setSinkId() on some platforms.
    const normalizedId = sinkId === 'default' ? '' : sinkId;
    await (this.ctx as any)
      .setSinkId(normalizedId)
      .catch(async (err: unknown) => {
        const isNotFound =
          err instanceof Error &&
          (err.name === 'NotFoundError' || err.message.includes('not found'));
        if (isNotFound && normalizedId !== '') {
          // Stored device ID is stale (unplugged / OS re-enumerated). Fall back
          // to the system default silently — this is a normal user-side condition.
          console.warn(
            '[AudioContextRinger] setSinkId: device not found, falling back to default',
            { label: this.label, sinkId },
          );
          await (this.ctx as any)
            .setSinkId('')
            .catch((fallbackErr: unknown) => {
              const msg =
                fallbackErr instanceof Error
                  ? fallbackErr.message
                  : String(fallbackErr);
              console.error(
                '[AudioContextRinger] setSinkId fallback error',
                msg,
              );
              Sentry.captureException(
                fallbackErr instanceof Error ? fallbackErr : new Error(msg),
                { data: { label: this.label, sinkId } },
              );
            });
        } else {
          const msg = err instanceof Error ? err.message : String(err);
          console.error('[AudioContextRinger] setSinkId error', msg);
          Sentry.captureException(err instanceof Error ? err : new Error(msg), {
            data: { label: this.label, sinkId },
          });
        }
      });
  }

  get sinkId(): string {
    return (this.ctx as any).sinkId ?? '';
  }

  async play(): Promise<void> {
    this.wantPlaying = true;
    const RESUME_TIMEOUT_MS = 2000;
    const resumed = await Promise.race([
      this.ctx.resume().then(() => true),
      new Promise<false>((resolve) =>
        setTimeout(() => resolve(false), RESUME_TIMEOUT_MS),
      ),
    ]);
    if (!this.wantPlaying) return;
    if (!resumed || this.ctx.state !== 'running') {
      const err = new Error(
        resumed
          ? `AudioContext not running after resume() (state: ${this.ctx.state})`
          : `AudioContext resume() timed out after ${RESUME_TIMEOUT_MS}ms — autoplay blocked`,
      );
      Sentry.withScope((scope) => {
        scope.setLevel('warning');
        scope.setContext('audio', {
          label: this.label,
          url: this.currentUrl,
          state: this.ctx.state,
          timedOut: !resumed,
        });
        Sentry.captureException(err);
      });
      throw err;
    }
    // loader .then sets buffer; loop as setSrc() may swap the load mid-wait
    while (this.bufferLoading) await this.bufferLoading;
    if (!this.wantPlaying || !this.buffer || !this.paused) return;
    const source = this.ctx.createBufferSource();
    source.buffer = this.buffer;
    source.loop = true;
    source.connect(this.gainNode);
    source.start(0);
    this.source = source;
    if (audioDiagnosticsEnabled) {
      Sentry.addBreadcrumb({
        category: 'audio',
        message: 'AudioContextRinger play()',
        level: 'info',
        data: { label: this.label, url: this.currentUrl, sinkId: this.sinkId },
      });
    }
  }

  pause(): void {
    this.wantPlaying = false;
    this._stopSource();
  }

  private _stopSource(): void {
    if (!this.source) return;
    try {
      this.source.stop(0);
    } catch (_) {
      // source may already be stopped
    }
    this.source.disconnect();
    this.source = null;
  }

  get paused(): boolean {
    return this.source === null;
  }

  async resume(): Promise<void> {
    await this.ctx.resume();
  }

  setSrc(url: string): void {
    this._stopSource();
    this.buffer = null;
    this.currentUrl = url;
    const loading = this._fetchBuffer(url);
    this.bufferLoading = loading;
    loading
      .then((buf) => {
        if (this.bufferLoading !== loading) return;
        this.buffer = buf;
        this.bufferLoading = null;
        if (this.wantPlaying) this.play().catch(console.warn);
      })
      .catch((err) => {
        console.error('[AudioContextRinger] setSrc error', err);
        if (this.bufferLoading === loading) this.bufferLoading = null;
      });
  }
}

let outboundRinging: AudioContextRinger | null = null;
let inboundRinging: AudioContextRinger | null = null;
let testInboundRinging: AudioContextRinger | null = null;

export const setInboundRingtone = (fileName: string, test = false) => {
  if (!test && fileName === currentInboundRingtone) return;
  if (!test) {
    nvStorage.set('selectedInboundRingtone', fileName);
    currentInboundRingtone = fileName;
  }
  getInboundRinging(test).setSrc(`${audioPath}/inbound/${fileName}`);
};

export const getInboundRinging = (test = false): AudioContextRinger => {
  if (test) {
    if (!testInboundRinging) {
      testInboundRinging = new AudioContextRinger(
        `${audioPath}/inbound/${currentInboundRingtone}`,
        'inbound-test',
      );
    }
    return testInboundRinging;
  }
  if (!inboundRinging) {
    inboundRinging = new AudioContextRinger(
      `${audioPath}/inbound/${currentInboundRingtone}`,
      'inbound',
    );
    inboundRinging.setGain(currentInboundRingingVolume);
  }
  return inboundRinging;
};

export const getOutboundRinging = (): AudioContextRinger => {
  if (!outboundRinging) {
    outboundRinging = new AudioContextRinger(
      `${audioPath}/ringing.wav`,
      'outbound',
    );
    outboundRinging.setGain(currentOutputVolume);
  }
  return outboundRinging;
};

export const peekOutboundRinging = (): AudioContextRinger | null =>
  outboundRinging;
export const peekInboundRinging = (): AudioContextRinger | null =>
  inboundRinging;

export const setOutputVolume = (volume: number): void => {
  currentOutputVolume = volume;
  outboundRinging?.setGain(volume);
};

export const setInboundRingingVolume = (volume: number): void => {
  currentInboundRingingVolume = volume;
  inboundRinging?.setGain(volume);
  testInboundRinging?.setGain(volume);
};

export const getInboundRingtoneFileName = () => currentInboundRingtone;
